import { AuthGate } from '@servicetim/ui/lib';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from '@/components/layout/app-shell';
import { CallbackPage } from './pages/callback';
import { NotFoundPage } from './pages/not-found';
import { SignedOutPage } from './pages/signed-out';

/**
 * Code-based route tree (no file-based routing, no codegen). Pages are lazy so the shell and the
 * help center load first.
 *
 *   /                                              help center
 *   /projects/$projectId/request/$issueTypeId      new request
 *   /requests, /requests/$idOrKey[/created]        my requests, detail, success
 *   /organization, /my-work                        organization requests, cross-project view
 *   /approvals, /notifications, /settings/notifications, /profile
 *   /callback, /signed-out                         outside the auth gate
 */

function RootLayout() {
  return <Outlet />;
}

function AppLayout() {
  return (
    <AuthGate>
      <AppShell>
        <Outlet />
      </AppShell>
    </AuthGate>
  );
}

const rootRoute = createRootRoute({ component: RootLayout });

/** Pathless layout route: contributes <AuthGate> + <AppShell> without a URL segment. */
const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppLayout });

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/home'), 'HomePage'),
});

const newRequestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects/$projectId/request/$issueTypeId',
  component: lazyRouteComponent(() => import('./pages/new-request'), 'NewRequestPage'),
});

const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/requests',
  component: lazyRouteComponent(() => import('./pages/requests'), 'MyRequestsPage'),
});

const requestDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/requests/$idOrKey',
  component: lazyRouteComponent(() => import('./pages/request-detail'), 'RequestDetailPage'),
});

export interface RequestCreatedSearch {
  failedUploads?: number;
}

const requestCreatedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/requests/$idOrKey/created',
  validateSearch: (search: Record<string, unknown>): RequestCreatedSearch => {
    const n = Number(search.failedUploads);
    return Number.isInteger(n) && n > 0 ? { failedUploads: n } : {};
  },
  component: lazyRouteComponent(() => import('./pages/request-created'), 'RequestCreatedPage'),
});

const organizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization',
  component: lazyRouteComponent(() => import('./pages/organization'), 'OrganizationPage'),
});

const myWorkRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/my-work',
  component: lazyRouteComponent(() => import('./pages/my-work'), 'MyWorkPage'),
});

const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/approvals',
  component: lazyRouteComponent(() => import('./pages/approvals'), 'ApprovalsPage'),
});

const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('./pages/notifications'), 'NotificationsPage'),
});

const notificationSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/notifications',
  component: lazyRouteComponent(
    () => import('./pages/notification-settings'),
    'NotificationSettingsPage',
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  component: lazyRouteComponent(() => import('./pages/profile'), 'ProfilePage'),
});

/* Bolt 1 scaffold paths: keep old bookmarks working. */
const legacyTicketsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tickets',
  beforeLoad: () => {
    throw redirect({ to: '/requests' });
  },
});
const legacyNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/new',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});
const legacyMeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me',
  beforeLoad: () => {
    throw redirect({ to: '/profile' });
  },
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: CallbackPage,
});

const signedOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signed-out',
  component: SignedOutPage,
});

/** Email-link approval decision (S-07-10): no session, so it lives outside the auth gate. */
const approvalTokenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approvals/token/$token',
  component: lazyRouteComponent(() => import('./pages/approval-token'), 'ApprovalTokenPage'),
});

const routeTree = rootRoute.addChildren([
  appRoute.addChildren([
    indexRoute,
    newRequestRoute,
    requestsRoute,
    requestDetailRoute,
    requestCreatedRoute,
    organizationRoute,
    myWorkRoute,
    approvalsRoute,
    notificationsRoute,
    notificationSettingsRoute,
    profileRoute,
    legacyTicketsRoute,
    legacyNewRoute,
    legacyMeRoute,
  ]),
  callbackRoute,
  signedOutRoute,
  approvalTokenRoute,
]);

export const routes = {
  index: indexRoute,
  newRequest: newRequestRoute,
  requests: requestsRoute,
  requestDetail: requestDetailRoute,
  requestCreated: requestCreatedRoute,
};

export function createAppRouter() {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFoundPage,
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
